export const SUPPORTED_KEYBOARD_LAYOUTS = [
  'us',
  'gb',
  'de',
  'fr',
  'pl',
  'es',
  'it',
  'pt',
  'se',
  'no',
  'dk',
  'ru',
] as const;

export type SupportedKeyboardLayout =
  (typeof SUPPORTED_KEYBOARD_LAYOUTS)[number];

type KeyModifierTable = Record<string, string>;
type LayoutKeyTable = Record<string, KeyModifierTable>;

/** Shallow-merge key overrides onto a base layout; optional keys are removed. */
function extendLayout(
  base: LayoutKeyTable,
  overrides: LayoutKeyTable,
  omitKeys: readonly string[] = [],
): LayoutKeyTable {
  const layout: LayoutKeyTable = { ...base, ...overrides };
  for (const key of omitKeys) {
    delete layout[key];
  }
  return layout;
}

/** US QWERTY — shared base for other layouts. */
const LAYOUT_US: LayoutKeyTable = {
  Backquote: { '0': '`', '1': '~', '8': '`', '9': '~' },
  Digit1: { '0': '1', '1': '!', '8': '1', '9': '!' },
  Digit2: { '0': '2', '1': '@', '8': '2', '9': '@' },
  Digit3: { '0': '3', '1': '#', '8': '3', '9': '#' },
  Digit4: { '0': '4', '1': '$', '8': '4', '9': '$' },
  Digit5: { '0': '5', '1': '%', '8': '5', '9': '%' },
  Digit6: { '0': '6', '1': '^', '8': '6', '9': '^' },
  Digit7: { '0': '7', '1': '&', '8': '7', '9': '&' },
  Digit8: { '0': '8', '1': '*', '8': '8', '9': '*' },
  Digit9: { '0': '9', '1': '(', '8': '9', '9': '(' },
  Digit0: { '0': '0', '1': ')', '8': '0', '9': ')' },
  Minus: { '0': '-', '1': '_', '8': '-', '9': '_' },
  Equal: { '0': '=', '1': '+', '8': '=', '9': '+' },
  KeyQ: { '0': 'q', '1': 'Q', '8': 'Q', '9': 'q' },
  KeyW: { '0': 'w', '1': 'W', '8': 'W', '9': 'w' },
  KeyE: { '0': 'e', '1': 'E', '8': 'E', '9': 'e' },
  KeyR: { '0': 'r', '1': 'R', '8': 'R', '9': 'r' },
  KeyT: { '0': 't', '1': 'T', '8': 'T', '9': 't' },
  KeyY: { '0': 'y', '1': 'Y', '8': 'Y', '9': 'y' },
  KeyU: { '0': 'u', '1': 'U', '8': 'U', '9': 'u' },
  KeyI: { '0': 'i', '1': 'I', '8': 'I', '9': 'i' },
  KeyO: { '0': 'o', '1': 'O', '8': 'O', '9': 'o' },
  KeyP: { '0': 'p', '1': 'P', '8': 'P', '9': 'p' },
  BracketLeft: { '0': '[', '1': '{', '8': '[', '9': '{' },
  BracketRight: { '0': ']', '1': '}', '8': ']', '9': '}' },
  KeyA: { '0': 'a', '1': 'A', '8': 'A', '9': 'a' },
  KeyS: { '0': 's', '1': 'S', '8': 'S', '9': 's' },
  KeyD: { '0': 'd', '1': 'D', '8': 'D', '9': 'd' },
  KeyF: { '0': 'f', '1': 'F', '8': 'F', '9': 'f' },
  KeyG: { '0': 'g', '1': 'G', '8': 'G', '9': 'g' },
  KeyH: { '0': 'h', '1': 'H', '8': 'H', '9': 'h' },
  KeyJ: { '0': 'j', '1': 'J', '8': 'J', '9': 'j' },
  KeyK: { '0': 'k', '1': 'K', '8': 'K', '9': 'k' },
  KeyL: { '0': 'l', '1': 'L', '8': 'L', '9': 'l' },
  Semicolon: { '0': ';', '1': ':', '8': ';', '9': ':' },
  Quote: { '0': "'", '1': '"', '8': "'", '9': '"' },
  Backslash: { '0': '\\', '1': '|', '8': '\\', '9': '|' },
  IntlBackslash: { '0': '<', '1': '>', '8': '<', '9': '>' },
  KeyZ: { '0': 'z', '1': 'Z', '8': 'Z', '9': 'z' },
  KeyX: { '0': 'x', '1': 'X', '8': 'X', '9': 'x' },
  KeyC: { '0': 'c', '1': 'C', '8': 'C', '9': 'c' },
  KeyV: { '0': 'v', '1': 'V', '8': 'V', '9': 'v' },
  KeyN: { '0': 'n', '1': 'N', '9': 'n' },
  KeyM: { '0': 'm', '1': 'M', '8': 'M', '9': 'm' },
  Comma: { '0': ',', '1': '<', '8': ',', '9': '<' },
  Period: { '0': '.', '1': '>', '8': '.', '9': '>' },
  Slash: { '0': '/', '1': '?', '8': '/', '9': '?' },
  KeyB: { '0': 'b', '1': 'B', '8': 'B', '9': 'b' },
  Space: { '0': ' ', '1': ' ', '2': ' ', '8': ' ', '9': ' ', '10': ' ' },
};

const LAYOUT_DE = extendLayout(
  LAYOUT_US,
  {
    Digit2: { '0': '2', '1': '"', '2': '²', '8': '2', '9': '"', '10': '²' },
    Digit3: { '0': '3', '1': '§', '2': '³', '8': '3', '9': '§', '10': '³' },
    Digit6: { '0': '6', '1': '&', '8': '6', '9': '&' },
    Digit7: { '0': '7', '1': '/', '2': '{', '8': '7', '9': '/', '10': '{' },
    Digit8: { '0': '8', '1': '(', '2': '[', '8': '8', '9': '(', '10': '[' },
    Digit9: { '0': '9', '1': ')', '2': ']', '8': '9', '9': ')', '10': ']' },
    Digit0: { '0': '0', '1': '=', '2': '}', '8': '0', '9': '=', '10': '}' },
    Minus: {
      '0': 'ß',
      '1': '?',
      '2': '\\',
      '8': 'ẞ',
      '9': '?',
      '10': '\\',
    },
    KeyQ: { '0': 'q', '1': 'Q', '2': '@', '8': 'Q', '9': 'q', '10': '@' },
    KeyE: { '0': 'e', '1': 'E', '2': '€', '8': 'E', '9': 'e', '10': '€' },
    KeyY: { '0': 'z', '1': 'Z', '8': 'Z', '9': 'z' },
    BracketLeft: { '0': 'ü', '1': 'Ü', '8': 'Ü', '9': 'ü' },
    BracketRight: {
      '0': '+',
      '1': '*',
      '2': '~',
      '8': '+',
      '9': '*',
      '10': '~',
    },
    Semicolon: { '0': 'ö', '1': 'Ö', '8': 'Ö', '9': 'ö' },
    Quote: { '0': 'ä', '1': 'Ä', '8': 'Ä', '9': 'ä' },
    Backslash: { '0': '#', '1': "'", '8': '#', '9': "'" },
    IntlBackslash: { '0': '<', '1': '>', '2': '|', '8': '<', '10': '|' },
    KeyZ: { '0': 'y', '1': 'Y', '8': 'Y', '9': 'y' },
    KeyN: { '0': 'n', '1': 'N', '8': 'N', '9': 'n' },
    KeyM: { '0': 'm', '1': 'M', '2': 'µ', '8': 'M', '9': 'm', '10': 'µ' },
    Comma: { '0': ',', '1': ';', '8': ',', '9': ';' },
    Period: { '0': '.', '1': ':', '8': '.', '9': ':' },
    Slash: { '0': '-', '1': '_', '8': '-', '9': '_' },
    Backquote: { '1': '°', '9': '°' },
  },
  ['Equal'],
);

const LAYOUT_GB = extendLayout(LAYOUT_US, {
  Backquote: { '0': '`', '1': '¬', '2': '|', '8': '`', '9': '¬', '10': '|' },
  Digit2: { '0': '2', '1': '"', '8': '2', '9': '"' },
  Digit3: { '0': '3', '1': '£', '8': '3', '9': '£' },
  Digit4: { '0': '4', '1': '$', '2': '€', '8': '4', '9': '$', '10': '€' },
  Quote: { '0': "'", '1': '@', '8': "'", '9': '@' },
  Backslash: { '0': '#', '1': '~', '8': '#', '9': '~' },
  IntlBackslash: { '0': '\\', '1': '|', '8': '\\', '9': '|' },
  KeyN: { '0': 'n', '1': 'N', '8': 'N', '9': 'n' },
});

const LAYOUT_PL = extendLayout(LAYOUT_US, {
  KeyL: {
    '0': 'l',
    '1': 'L',
    '2': 'ł',
    '3': 'Ł',
    '8': 'L',
    '9': 'l',
    '10': 'Ł',
    '11': 'ł',
  },
  KeyE: {
    '0': 'e',
    '1': 'E',
    '2': 'ę',
    '3': 'Ę',
    '8': 'E',
    '9': 'e',
    '10': 'Ę',
    '11': 'ę',
  },
  KeyO: {
    '0': 'o',
    '1': 'O',
    '2': 'ó',
    '3': 'Ó',
    '8': 'O',
    '9': 'o',
    '10': 'Ó',
    '11': 'ó',
  },
  KeyA: {
    '0': 'a',
    '1': 'A',
    '2': 'ą',
    '3': 'Ą',
    '8': 'A',
    '9': 'a',
    '10': 'Ą',
    '11': 'ą',
  },
  KeyS: {
    '0': 's',
    '1': 'S',
    '2': 'ś',
    '3': 'Ś',
    '8': 'S',
    '9': 's',
    '10': 'Ś',
    '11': 'ś',
  },
  KeyZ: {
    '0': 'z',
    '1': 'Z',
    '2': 'ż',
    '3': 'Ż',
    '8': 'Z',
    '9': 'z',
    '10': 'Ż',
    '11': 'ż',
  },
  KeyX: {
    '0': 'x',
    '1': 'X',
    '2': 'ź',
    '3': 'Ź',
    '8': 'X',
    '9': 'x',
    '10': 'Ź',
    '11': 'ź',
  },
  KeyC: {
    '0': 'c',
    '1': 'C',
    '2': 'ć',
    '3': 'Ć',
    '8': 'C',
    '9': 'c',
    '10': 'Ć',
    '11': 'ć',
  },
  KeyN: {
    '0': 'n',
    '1': 'N',
    '2': 'ń',
    '3': 'Ń',
    '8': 'N',
    '9': 'n',
    '10': 'Ń',
    '11': 'ń',
  },
});

const LAYOUT_FR = extendLayout(
  LAYOUT_US,
  {
    Backquote: { '0': '²', '1': '~', '8': '²', '9': '~' },
    Digit1: { '0': '&', '1': '1', '8': '&', '9': '1' },
    Digit2: { '0': 'é', '1': '2', '2': '~', '8': 'É', '9': '2', '10': '~' },
    Digit3: { '0': '"', '1': '3', '2': '#', '8': '"', '9': '3', '10': '#' },
    Digit4: { '0': "'", '1': '4', '2': '{', '8': "'", '9': '4', '10': '{' },
    Digit5: { '0': '(', '1': '5', '2': '[', '8': '(', '9': '5', '10': '[' },
    Digit6: { '0': '-', '1': '6', '2': '|', '8': '-', '9': '6', '10': '|' },
    Digit7: { '0': 'è', '1': '7', '2': '`', '8': 'È', '9': '7', '10': '`' },
    Digit8: { '0': '_', '1': '8', '2': '\\', '8': '_', '9': '8', '10': '\\' },
    Digit9: { '0': 'ç', '1': '9', '2': '^', '8': 'Ç', '9': '9', '10': '^' },
    Digit0: { '0': 'à', '1': '0', '2': '@', '8': 'À', '9': '0', '10': '@' },
    Minus: { '0': ')', '1': '°', '2': ']', '8': ')', '9': '°', '10': ']' },
    Equal: { '0': '=', '1': '+', '2': '}', '8': '=', '9': '+', '10': '}' },
    KeyQ: { '0': 'a', '1': 'A', '8': 'A', '9': 'a' },
    KeyW: { '0': 'z', '1': 'Z', '8': 'Z', '9': 'z' },
    KeyE: { '0': 'e', '1': 'E', '2': '€', '8': 'E', '9': 'e', '10': '€' },
    BracketRight: {
      '0': '$',
      '1': '£',
      '2': '¤',
      '8': '$',
      '9': '£',
      '10': '¤',
    },
    KeyA: { '0': 'q', '1': 'Q', '8': 'Q', '9': 'q' },
    Semicolon: { '0': 'm', '1': 'M', '8': 'M', '9': 'm' },
    Quote: { '0': 'ù', '1': '%', '8': 'Ù', '9': '%' },
    Backslash: { '0': '*', '1': 'µ', '8': '*', '9': 'µ' },
    IntlBackslash: { '0': '<', '1': '>', '8': '<' },
    KeyZ: { '0': 'w', '1': 'W', '8': 'W', '9': 'w' },
    KeyN: { '0': 'n', '1': 'N', '8': 'N', '9': 'n' },
    KeyM: { '0': ',', '1': '?', '8': ',', '9': '?' },
    Comma: { '0': ';', '1': '.', '8': ';', '9': '.' },
    Period: { '0': ':', '1': '/', '8': ':', '9': '/' },
    Slash: { '0': '!', '1': '§', '8': '!', '9': '§' },
  },
  ['BracketLeft'],
);

/** Fixed search order for keystroke detection (us first). */
const KEYSTROKE_LAYOUT_ORDER = ['us', 'de', 'gb', 'pl', 'fr'] as const;

const LAYOUT_TABLES: Record<
  (typeof KEYSTROKE_LAYOUT_ORDER)[number],
  LayoutKeyTable
> = {
  us: LAYOUT_US,
  de: LAYOUT_DE,
  gb: LAYOUT_GB,
  pl: LAYOUT_PL,
  fr: LAYOUT_FR,
};

class KeyboardLayout {
  constructor(private readonly keyTable: LayoutKeyTable) {}

  static isPrintableCharacter(event: KeyboardEvent): boolean {
    return Boolean(event.key && event.key.length === 1);
  }

  static generateModifiersFlag(event: KeyboardEvent): number {
    const shiftFlag = event.getModifierState('Shift') ? 1 : 0;
    const altFlag = event.getModifierState('AltGraph') ? 2 : 0;
    const ctrlFlag = event.getModifierState('Control') ? 4 : 0;
    const capsLockFlag = event.getModifierState('CapsLock') ? 8 : 0;
    return shiftFlag + altFlag + ctrlFlag + capsLockFlag;
  }

  containsKey(event: KeyboardEvent): boolean {
    if (!KeyboardLayout.isPrintableCharacter(event)) {
      return false;
    }
    const key = this.keyTable[event.code];
    if (key === undefined) {
      return false;
    }
    const modifiersFlag = KeyboardLayout.generateModifiersFlag(event);
    const expected = key[String(modifiersFlag)];
    return expected !== undefined && expected === event.key;
  }
}

export class KeyboardLayoutManager {
  currentLayoutName: string;
  private readonly allowedLayouts: ReadonlySet<string>;
  private readonly layouts: Record<string, KeyboardLayout>;

  constructor(
    initialLayout = 'us',
    allowedLayouts: readonly string[] = SUPPORTED_KEYBOARD_LAYOUTS,
  ) {
    this.allowedLayouts = new Set(allowedLayouts);
    this.layouts = Object.fromEntries(
      KEYSTROKE_LAYOUT_ORDER.map((name) => [
        name,
        new KeyboardLayout(LAYOUT_TABLES[name]),
      ]),
    );
    this.currentLayoutName = this.allowedLayouts.has(initialLayout)
      ? initialLayout
      : 'us';
  }

  static isPrintableCharacter(event: KeyboardEvent): boolean {
    return KeyboardLayout.isPrintableCharacter(event);
  }

  private isAllowed(layout: string): boolean {
    return this.allowedLayouts.has(layout);
  }

  setCurrentLayout(layout: string | null | undefined): boolean {
    if (layout && typeof layout === 'string' && this.isAllowed(layout)) {
      this.currentLayoutName = layout;
      return true;
    }
    return false;
  }

  detectFromKeystroke(event: KeyboardEvent): string | null {
    if (event.metaKey || event.ctrlKey) {
      return null;
    }
    if (!KeyboardLayoutManager.isPrintableCharacter(event)) {
      return null;
    }

    const currentLayout = this.layouts[this.currentLayoutName];
    if (currentLayout?.containsKey(event)) {
      return this.currentLayoutName;
    }

    for (const name of KEYSTROKE_LAYOUT_ORDER) {
      if (!this.isAllowed(name)) {
        continue;
      }
      if (this.layouts[name]?.containsKey(event)) {
        this.currentLayoutName = name;
        return name;
      }
    }

    return this.currentLayoutName;
  }

  detectFromLayoutMap(layoutMap: {
    get(code: string): string | undefined;
  }): string | null {
    let detected: string | null = null;

    if (layoutMap.get('KeyQ') === 'a') {
      detected = 'fr';
    } else if (layoutMap.get('KeyY') === 'z') {
      detected = 'de';
    } else if (
      layoutMap.get('Backslash') === '#' ||
      layoutMap.get('Digit3') === '£' ||
      layoutMap.get('IntlBackslash') === '\\'
    ) {
      detected = 'gb';
    } else if (layoutMap.get('Semicolon') === 'ñ') {
      detected = 'es';
    } else if (
      layoutMap.get('BracketLeft') === 'è' &&
      layoutMap.get('Semicolon') === 'ò'
    ) {
      detected = 'it';
    } else if (layoutMap.get('Semicolon') === 'ç') {
      detected = 'pt';
    } else if (layoutMap.get('BracketLeft') === 'å') {
      if (layoutMap.get('Quote') === 'ä') {
        detected = 'se';
      } else if (layoutMap.get('Semicolon') === 'ø') {
        detected = 'no';
      } else if (layoutMap.get('Semicolon') === 'æ') {
        detected = 'dk';
      } else {
        detected = 'se';
      }
    } else if (layoutMap.get('KeyQ') === 'й') {
      detected = 'ru';
    } else if (
      typeof navigator !== 'undefined' &&
      navigator.language?.toLowerCase().startsWith('pl')
    ) {
      detected = 'pl';
    } else {
      detected = 'us';
    }

    if (detected && this.isAllowed(detected)) {
      return detected;
    }
    return null;
  }

  async initFromBrowser(): Promise<string | null> {
    if (typeof navigator === 'undefined' || !('keyboard' in navigator)) {
      return null;
    }
    const keyboard = (
      navigator as unknown as {
        keyboard?: {
          getLayoutMap?: () => Promise<{
            get(code: string): string | undefined;
          }>;
        };
      }
    ).keyboard;

    if (typeof keyboard?.getLayoutMap !== 'function') {
      return null;
    }

    try {
      const layoutMap = await keyboard.getLayoutMap();
      if (layoutMap && typeof layoutMap.get === 'function') {
        const detected = this.detectFromLayoutMap(layoutMap);
        if (detected) {
          this.setCurrentLayout(detected);
          return detected;
        }
      }
    } catch {
      // Ignore unsupported or denied layout map queries
    }
    return null;
  }
}
